import { Trans, useLingui } from '@lingui/react/macro';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@repo/videa-components/src/ui/select';

import {
  GINGIVAL_MARGIN_CORONAL_DISPLAY_MODES,
  type GingivalMarginCoronalDisplayMode,
} from '@/common-components/composed/PerioChart/types';

import type { PreferenceUpdateBody } from '../../../@types/preferences';
import { usePreferences } from '../../../react-query/usePreferences';
import { mixpanelEvents, useTrackEvent } from '../../../utils/mixpanel';

interface GingivalMarginDisplayPreferenceProps {
  onChange: (update: PreferenceUpdateBody) => void;
}

export const GingivalMarginDisplayPreference = ({
  onChange,
}: GingivalMarginDisplayPreferenceProps) => {
  const { data: preferences } = usePreferences();
  const { t } = useLingui();
  const { trackEvent } = useTrackEvent();

  const labels: Record<GingivalMarginCoronalDisplayMode, string> = {
    coronal_minus_sign: t`Minus sign (-X)`,
    coronal_plus_sign: t`Plus sign (+X)`,
  };

  const descriptions: Record<GingivalMarginCoronalDisplayMode, string> = {
    coronal_minus_sign: t`Shows coronal tissue as -X (e.g., probing depth of 3 and gingival margin of -1 means clinical attachment level of 2).`,
    coronal_plus_sign: t`Shows coronal tissue as +X (e.g., probing depth of 3 and gingival margin of +1 means clinical attachment level of 2).`,
  };

  return (
    <div className="vai-flex vai-w-full vai-flex-col vai-gap-1">
      <p className="vai-typography-small !vai-mb-0">
        <Trans>Gingival margin display (coronal)</Trans>
      </p>
      <p className="vai-text-sm vai-text-zinc-500 !vai-mb-2">
        <Trans>
          Choose how coronal tissue is represented in the perio chart. Different
          practice management systems use different conventions.
        </Trans>
      </p>
      <Select
        value={
          preferences?.gingivalMarginCoronalDisplayMode ?? 'coronal_minus_sign'
        }
        onValueChange={(value: GingivalMarginCoronalDisplayMode) => {
          trackEvent(mixpanelEvents.gingivalMarginDisplayPreferenceUpdate, {
            gingivalMarginCoronalDisplayMode: value,
          });
          onChange({ gingivalMarginCoronalDisplayMode: value });
        }}
      >
        <SelectTrigger className="vai-max-w-xs vai-text-left">
          <SelectValue
            placeholder={t`Select display mode`}
            className="vai-sr-only"
          />
        </SelectTrigger>
        <SelectContent>
          {GINGIVAL_MARGIN_CORONAL_DISPLAY_MODES.map((mode) => (
            <SelectItem key={mode} value={mode}>
              <span className="vai-block">{labels[mode]}</span>
              <span className="vai-block vai-text-zinc-500 vai-text-xs">
                {descriptions[mode]}
              </span>
            </SelectItem>
          ))}
        </SelectContent>
      </Select>
    </div>
  );
};
